import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { api } from '../../../services/api'
import { useMutation, useQueryClient } from 'react-query'
import { useState } from 'react'
import { CloseIcon } from '@chakra-ui/icons'
import { base64ToBlob } from '../../../utils/blob'

const schema = yup.object().shape({
  nome_suporte: yup.string().required('Campo obrigatório'),
  id_chamado: yup.string().required('Campo obrigatório'),
  resolucao_chamado: yup.string().required('Campo obrigatório'),
  solucao_detalhada: yup.string().required('Campo obrigatório'),
})

export function FinalizarChamado({ item, insideModal = false }) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [screenshot, setScreenshot] = useState(null)

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
  })

  const toast = useToast()

  const queryClient = new useQueryClient()

  const { mutateAsync } = useMutation(
    async (data) => {
      const blob = base64ToBlob(screenshot)

      const formData = new FormData()

      formData.append('imagem', blob)
      formData.append('nome_suporte', data.nome_suporte)
      formData.append('id_chamado', data.id_chamado)
      formData.append('resolucao_chamado', data.resolucao_chamado)
      formData.append('solucao_detalhada', data.solucao_detalhada)

      const response = await api.post('/site/v2/crm-controle/finalizar-chamado-sistema', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('listarChamadosSistema')
        toast({
          title: 'Sucesso',
          description: 'Chamado finalizado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })

        onClose()
      },
      onError: (error) => {
        toast({
          title: 'Erro',
          description: error?.response?.data?.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        })
      },
    }
  )

  function handlePaste(e) {
    const items = e.clipboardData.items

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const blob = items[i].getAsFile()

        const reader = new FileReader()

        reader.onload = function (event) {
          const base64Image = event.target.result

          setScreenshot(base64Image)
        }

        reader.readAsDataURL(blob)
      }
    }
  }

  // Ajustando o botão com base na propriedade insideModal
  const buttonProps = insideModal
    ? {
        colorScheme: 'red',
        size: 'md',
      }
    : {
        flex: 1,
        size: 'sm',
      }

  return (
    <>
      <Button onClick={onOpen} {...buttonProps}>
        Finalizar Chamado
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Finalizar chamado {item?.tipo_chamado} - Numero {item?.numero_chamado}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <form>
              <Flex flexDir="column" gap={2}>
                <Input type="hidden" {...register('id_chamado')} value={item?.numero_chamado} />

                <Input {...register('nome_suporte')} placeholder="Nome do responsável pelo chamado" />
                {errors.nome_suporte && <span style={{ color: 'red', fontSize: '.7rem' }}>{errors.nome_suporte.message}</span>}

                <Select {...register('resolucao_chamado')}>
                  <option value="" disabled selected>
                    Selecione a resolução do chamado
                  </option>

                  <option value="Falha no sistema">Falha no sistema</option>
                  <option value="Usuario">Usuario</option>
                  <option value="Outros">Outros</option>
                </Select>
                {errors.resolucao_chamado && <span style={{ color: 'red', fontSize: '.7rem' }}>{errors.resolucao_chamado.message}</span>}

                <Box position="relative" overflow="hidden" borderRadius={5}>
                  <Textarea
                    {...register('solucao_detalhada')}
                    placeholder="Digite a solução detalhada do chamado"
                    resize="none"
                    rows={8}
                    onPaste={handlePaste}
                    paddingRight={screenshot && '100px'}
                  />

                  {screenshot && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        width: '150px',
                        height: '150px',
                        objectFit: 'contain',
                        zIndex: '1000',
                      }}
                    >
                      <img src={screenshot} alt="Screenshot" />
                      <Icon
                        position="absolute"
                        top="1"
                        right="1"
                        as={CloseIcon}
                        w={3}
                        h={3}
                        cursor="pointer"
                        onClick={() => setScreenshot(null)}
                        borderRadius="50%"
                        border="1px solid"
                        borderColor="white"
                        color="white"
                      />
                    </div>
                  )}
                </Box>

                {errors.solucao_detalhada && <span style={{ color: 'red', fontSize: '.7rem' }}>{errors.solucao_detalhada.message}</span>}
              </Flex>
            </form>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={handleSubmit(mutateAsync)} isLoading={isSubmitting}>
              Finalizar Chamado
            </Button>
            <Button colorScheme="red" mr={3} onClick={onClose}>
              Cancelar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}
